import React, { useState, useEffect } from 'react';
import { CheckCircle2, Heart, Shield, DollarSign, Phone, Calendar, Stethoscope, BadgeCheck, Users, TrendingDown, Sparkles } from 'lucide-react';
import ConsultationForm from '../components/ConsultationForm';
import ExitIntentPopup from '../components/ExitIntentPopup';
import MobilePhoneFAB from '../components/MobilePhoneFAB';
import CookieConsent from '../components/CookieConsent';
import { credentialBadges } from '../constants/credentialBadges';
import '../styles/firstadvantage.css';

const FirstAdvantagePage = () => {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitPopupShown, setExitPopupShown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };

    let pageLoadTime = Date.now();
    const handleMouseLeave = (e) => {
      const timeOnPage = Date.now() - pageLoadTime;
      if (e.clientY <= 0 && timeOnPage > 5000 && !exitPopupShown && !showThankYou && !showExitPopup) {
        setShowExitPopup(true);
        setExitPopupShown(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [showThankYou, exitPopupShown, showExitPopup]);

  const handleFormSubmit = (email) => {
    setSubmittedEmail(email);
    setShowThankYou(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToForm = () => {
    document.getElementById('fa-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const openCalendly = () => {
    window.Calendly?.initPopupWidget({url: 'https://calendly.com/d/cxnm-px9-pj8/new-meeting'});
  };

  if (showThankYou) {
    return (
      <div className="fa-page">
        <ThankYouSection email={submittedEmail} />
        <FAFooter credentialBadges={credentialBadges} />
      </div>
    );
  }

  return (
    <div className="fa-page">
      <ExitIntentPopup 
        isOpen={showExitPopup} 
        onClose={() => setShowExitPopup(false)}
        onSubmit={() => setShowExitPopup(false)}
      />
      <MobilePhoneFAB />

      {showStickyCTA && (
        <div className="fa-sticky-bar">
          <div className="fa-sticky-content">
            <span className="fa-sticky-text">Zero Deductible Healthcare Awaits</span>
            <div className="fa-sticky-actions">
              <a href="tel:+18558088689" className="fa-sticky-phone">
                <Phone size={18} />
                (855) 808-8689
              </a>
              <button className="fa-sticky-btn" onClick={scrollToForm}>
                Get Your Quote
              </button>
            </div>
          </div>
        </div>
      )}

      <HeroSection onFormSubmit={handleFormSubmit} openCalendly={openCalendly} />
      <BenefitsOverview />
      <HowItWorks />
      <SavingsSection />
      <TestimonialsSection />
      <FinalCTASection onFormSubmit={handleFormSubmit} />
      <FAFooter credentialBadges={credentialBadges} />
      <CookieConsent />
    </div>
  );
};

const FAHeader = () => (
  <header className="fa-header">
    <div className="fa-header-content">
      <a href="https://www.amplifyhrm.com" className="fa-logo" target="_blank" rel="noopener noreferrer">
        <img 
          src="https://customer-assets.emergentagent.com/job_69a9ffbf-3b0d-4791-80d1-6eb6aacb68ee/artifacts/5dacw0dj_Amplify%20-%20Logo.jpg" 
          alt="Amplify HR Logo"
          loading="eager"
        />
      </a>
      <div className="fa-header-badge">
        <span>Healthcare that works for your business AND your employees</span>
      </div>
    </div>
  </header>
);

const HeroSection = ({ onFormSubmit, openCalendly }) => (
  <section className="fa-hero">
    <div className="fa-hero-bg">
      <div className="fa-hero-circles">
        <div className="fa-circle fa-circle-1"></div>
        <div className="fa-circle fa-circle-2"></div>
        <div className="fa-circle fa-circle-3"></div>
      </div>
    </div>
    
    <div className="fa-hero-content">
      <div className="fa-hero-text">
        <div className="fa-hero-tag">
          <Shield size={18} />
          <span>FIRSTADVANTAGE™ BY AMPLIFY HR</span>
        </div>
        <h1 className="fa-hero-headline">
          <span className="fa-hero-zero">$0</span> Deductible Health Plans
        </h1>
        <p className="fa-hero-subheadline">
          Give your employees exceptional healthcare without the financial burden. 
          Our proprietary FirstAdvantage™ program delivers <span className="fa-highlight">zero-deductible</span> options 
          that attract and retain top talent.
        </p>
        
        <div className="fa-hero-features">
          <div className="fa-hero-feature">
            <CheckCircle2 size={20} />
            <span>No deductibles on most plans</span>
          </div>
          <div className="fa-hero-feature">
            <CheckCircle2 size={20} />
            <span>Multiple carrier options</span>
          </div>
          <div className="fa-hero-feature">
            <CheckCircle2 size={20} />
            <span>Custom GAP insurance available</span>
          </div>
        </div>

        <div className="fa-hero-phone">
          <Phone size={20} />
          <span>Questions? Call us: </span>
          <a href="tel:+18558088689">(855) 808-8689</a>
        </div>
      </div>
      
      <div className="fa-hero-form" id="fa-form">
        <div className="fa-form-header">
          <h3>See Your Benefits Options</h3>
          <div className="fa-form-heart-icon">
            <Heart size={44} />
          </div>
          <p>Free consultation, no obligation</p>
        </div>
        <ConsultationForm onSubmit={onFormSubmit} source="firstadvantage" />
        <div className="fa-form-footer">
          <span>or</span>
          <button onClick={openCalendly} className="fa-calendly-btn">
            <Calendar size={18} />
            Schedule a benefits review
          </button>
        </div>
      </div>
    </div>
  </section>
);

const BenefitsOverview = () => (
  <section className="fa-benefits">
    <div className="fa-container">
      <div className="fa-benefits-header">
        <h2>Why FirstAdvantage™ Benefits?</h2>
      </div>
      
      <div className="fa-mission-statement">
        <p>FirstAdvantage™ by Amplify HR helps employers optimize group health benefits, reduce premium costs, and improve employee experience through integrated benefits strategy, consulting, and administration.</p>
      </div>
      
      <div className="fa-benefits-grid">
        <div className="fa-benefit-card">
          <div className="fa-benefit-icon">
            <DollarSign size={36} />
          </div>
          <h3>Zero Deductible Plans</h3>
          <p>Employees get care without worrying about meeting a deductible first. Healthcare that's actually usable.</p>
        </div>
        
        <div className="fa-benefit-card">
          <div className="fa-benefit-icon">
            <Users size={36} />
          </div>
          <h3>Attract Top Talent</h3>
          <p>Stand out in recruiting with benefits that employees actually want. Retention improves dramatically.</p>
        </div>
        
        <div className="fa-benefit-card">
          <div className="fa-benefit-icon">
            <Stethoscope size={36} />
          </div>
          <h3>Multiple Carriers</h3>
          <p>Unlike big-box PEOs, we're not captive to one carrier. We shop the market for your best options.</p>
        </div>
        
        <div className="fa-benefit-card">
          <div className="fa-benefit-icon">
            <Shield size={36} />
          </div>
          <h3>GAP Insurance</h3>
          <p>Custom supplemental coverage fills the gaps, giving employees comprehensive protection.</p>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="fa-how-it-works">
    <div className="fa-container">
      <h2>How FirstAdvantage™ Works</h2>
      
      <div className="fa-steps">
        <div className="fa-step">
          <div className="fa-step-number">01</div>
          <div className="fa-step-content">
            <h3>Benefits Review</h3>
            <p>We analyze your current benefits and employee needs to identify the best options for your team.</p>
          </div>
        </div>
        
        <div className="fa-step">
          <div className="fa-step-number">02</div>
          <div className="fa-step-content">
            <h3>Custom Plan Design</h3>
            <p>We build a benefits package with zero-deductible options, multiple tiers, and supplemental coverage.</p>
          </div>
        </div>
        
        <div className="fa-step">
          <div className="fa-step-number">03</div>
          <div className="fa-step-content">
            <h3>Seamless Implementation</h3>
            <p>Our dedicated team handles enrollment, communications, and ongoing support—you focus on your business.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const SavingsSection = () => (
  <section className="fa-savings">
    <div className="fa-container">
      <div className="fa-savings-content">
        <div className="fa-savings-text">
          <h2>Better Benefits, Better Costs</h2>
          <p>FirstAdvantage™ isn't just better for employees—it's smart for your bottom line.</p>
          
          <div className="fa-savings-stats">
            <div className="fa-savings-stat">
              <div className="fa-savings-number">15-40%</div>
              <div className="fa-savings-label">Average PEO Savings</div>
            </div>
            <div className="fa-savings-stat">
              <div className="fa-savings-number">$0</div>
              <div className="fa-savings-label">Employee Deductibles</div>
            </div>
            <div className="fa-savings-stat">
              <div className="fa-savings-number">100%</div>
              <div className="fa-savings-label">Transparent Pricing</div>
            </div>
          </div>
        </div>
        
        <div className="fa-savings-visual">
          <div className="fa-savings-card">
            <BadgeCheck size={48} />
            <h3>IRS Certified PEO</h3>
            <p>Your business is protected with a certified, accredited PEO partner.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="fa-testimonials">
    <div className="fa-container">
      <h2>What Our Clients Say</h2>
      
      <div className="fa-testimonial-cards">
        <div className="fa-testimonial">
          <div className="fa-testimonial-quote">
            "The FirstAdvantage benefits program was a game-changer for recruiting. Candidates are impressed when they see zero-deductible health plans."
          </div>
          <div className="fa-testimonial-author">
            <div className="fa-testimonial-avatar">YB</div>
            <div>
              <div className="fa-testimonial-name">YB</div>
              <div className="fa-testimonial-company">72 employees, professional services</div>
            </div>
          </div>
        </div>
        
        <div className="fa-testimonial">
          <div className="fa-testimonial-quote">
            "Our employees love the benefits. No more worrying about deductibles—they can actually use their healthcare without financial stress."
          </div>
          <div className="fa-testimonial-author">
            <div className="fa-testimonial-avatar">HB</div>
            <div>
              <div className="fa-testimonial-name">HB</div>
              <div className="fa-testimonial-company">25 employees, professional services</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FinalCTASection = ({ onFormSubmit }) => (
  <section className="fa-final-cta">
    <div className="fa-container">
      <div className="fa-final-content">
        <div className="fa-final-tagline">
          Healthcare that works for your business AND your employees
        </div>
        <div className="fa-final-icon">
          <Heart size={48} />
        </div>
        <h2>Give Your Team the Benefits They Deserve</h2>
        <p>Zero-deductible healthcare isn't a dream—it's FirstAdvantage™ by Amplify HR</p>
        
        <div className="fa-final-form">
          <ConsultationForm onSubmit={onFormSubmit} source="firstadvantage" />
        </div>
        
        <div className="fa-final-trust">
          <span>Certified • Accredited • Award-Winning</span>
        </div>
      </div>
    </div>
  </section>
);

const ThankYouSection = ({ email }) => (
  <section className="fa-thankyou">
    <div className="fa-container">
      <div className="fa-thankyou-icon">
        <CheckCircle2 size={80} />
      </div>
      <h1>Your Benefits Review Is Scheduled</h1>
      <p>Thanks, {email}. A FirstAdvantage™ specialist will contact you within 24 hours to discuss your benefits options.</p>
      
      <div className="fa-thankyou-info">
        <h3>In your consultation, we'll cover:</h3>
        <ul>
          <li><CheckCircle2 size={18} /> Zero-deductible plan options for your team</li>
          <li><CheckCircle2 size={18} /> Cost comparison with your current benefits</li>
          <li><CheckCircle2 size={18} /> GAP insurance and supplemental coverage</li>
          <li><CheckCircle2 size={18} /> Implementation timeline and process</li>
        </ul>
      </div>
    </div>
  </section>
);

const FAFooter = ({ credentialBadges }) => (
  <footer className="fa-footer">
    <div className="fa-footer-content">
      <div className="fa-footer-badges">
        {credentialBadges.map((badge) => (
          <div key={badge.alt} className="fa-footer-badge">
            <img src={badge.src} alt={badge.alt} loading="lazy" />
          </div>
        ))}
      </div>
      <p>© 2026 Amplify HR. FirstAdvantage™ is a proprietary benefits program.</p>
    </div>
  </footer>
);

export default FirstAdvantagePage;
